import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import MessageModal from '../../../components/MessageModal';
import { ReduxStates } from '../../../redux/reduxTypes';
import { mainStyles, cardTitleStyles, paragraphStyles } from '../config';
import MappingTable from './MappingTable';
import { ReactComponent as HelpIcon } from './../../../assets/icons/help-icon.svg';

interface Props {
  setStatus: (arg1: any) => void;
}

const DataMapping = ({ setStatus }: Props): React.ReactElement => {
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [showModal, setShowModal] = useState<boolean>(false);

  const InfoComponent: React.FC = () => {
    return (
      <div className="font-nunitoLight text-sm py-[12px] px-[24px] relative z-30">
        <p className={`${cardTitleStyles} mb-2`}>Data Mapping</p>
        <p className={paragraphStyles}>
          After uploading your file, please help us understand your data so that we may store and display it properly.
          This advising is necessary in order to filter your data accurately across the platform. Input is needed for
          any "Status" containing a red exclamation point.
        </p>
        <p className={paragraphStyles}>A value is not recognized when:</p>
        <ul className="list-[disc] pl-4 mb-1">
          <li>The value does not exist in our database.</li>
          <li>The value is written as an abbreviation.</li>
          <li>There is a misspelling.</li>
        </ul>
        <p className={` ${paragraphStyles} mb-4`}>
          Any values that are not recognized are featured in the "Your Value" field. Please choose an option from the
          "Mapped Value" drop-down to tell us how to handle it.
        </p>
        <p className={`${cardTitleStyles} mb-2`}>New Value</p>
        <ul className="list-[disc] pl-4 mb-1">
          <li>
            <p className={paragraphStyles}>
              Choose the "New Value" option when your value does not equal another option in the drop-down.
            </p>
            <p className={` ${paragraphStyles} mb-4`}>
              Your value will be treated as a new value and will be added as an option in the filter modal for that
              field.
            </p>
          </li>
          <li>
            You’ll see “New Value” in the drop-down and can click that. It will show “New Value” in the Mapped Value
            drop-down before clicking “Save Changes”. It will show the actual new value in “Mapped Value” after clicking
            “Save Changes”.
            <ul className="list-[disc] pl-4 mb-1">
              <li>
                For example, I want to make a new value for “Your Value” of ‘Candle'. I will click the “Mapped Value”
                drop-down of ‘Candle’ and choose the ‘New Value’ option at the top of the drop-down. When I click
                outside of the drop-down, it will show ‘New Value’. After I click “Save Changes”, it will show ‘Candle’
                in the mapped value drop-down instead.
              </li>
            </ul>
          </li>
        </ul>
        <p className={`${cardTitleStyles} mb-2`}>Another Existing Value</p>
        <p className={paragraphStyles}>
          All other options in the drop-down are found within our database. Choose one of these options when your value
          is equal to an existing value.
        </p>
        <p className={paragraphStyles}>
          When viewing data on other screens, filter by that chosen value in the filter modal. Data from your file will
          be mapped to that value.
        </p>
        <p className={paragraphStyles}>
          After making updates in the "Mapped Value" drop-downs, click "Save Changes" at the bottom left to process
          these changes.
        </p>
      </div>
    );
  };

  return (
    <div className="block">
      <div className={mainStyles}>
        <div>
          <p className={`${cardTitleStyles} mb-4`}>{copyData?.data_mapping_title}</p>
        </div>
        <div className="w-full">
          <p className="font-nunitoLight text-sm inline mr-2">{copyData?.data_mapping_info}</p>
          <div className="inline-block pointer" role="button" onClick={() => setShowModal(true)}>
            <HelpIcon className={'inline-block'} />
          </div>
        </div>
      </div>
      <MappingTable setStatus={setStatus} />
      {showModal && (
        <MessageModal closeModal={() => setShowModal(false)}>
          <div>
            <InfoComponent />
          </div>
        </MessageModal>
      )}
    </div>
  );
};

export default DataMapping;
